import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
  Update,
} from '@reduxjs/toolkit';
import { difference, uniq } from 'lodash-es';

import { ListReportPanoramasWithConditionsResp } from '@/shared/api/protocol-ts/api/core/svc_report_pb';
import { Asset } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { LoadingStateType, SliceName } from '@/shared/config';

import type { PatientAssetsSnapshot, ReportAssetsSnapshot } from '../config';

export const assetsAdapter = createEntityAdapter<Asset>({
  selectId: (asset: Asset) => asset?.ID,
});

const makeInitialState = () => ({
  guardedIDs: [] as string[],
  loading: 'idle' as LoadingStateType,
  patientAssetsSnapshot: {
    patientID: '',
    loading: 'idle',
  } as PatientAssetsSnapshot,
  patientAssetsSnapshotCompletedGeneration: 0,
  reportAssetsSnapshot: {
    reportID: '',
    loading: 'idle',
  } as ReportAssetsSnapshot,
  reportAssetsSnapshotPatientCompletionAtRefreshStart: null as number | null,
});

const assetState = assetsAdapter.getInitialState(makeInitialState());

type AssetState = typeof assetState;

const assetsSlice = createSlice({
  name: SliceName.assets,
  initialState: assetState,
  reducers: {
    addOne: (state, action: PayloadAction<Asset>) =>
      assetsAdapter.addOne(state as AssetState, action.payload),
    addMany: (state, action: PayloadAction<Asset[]>) =>
      assetsAdapter.addMany(state as AssetState, action.payload),
    setAll: (state, action: PayloadAction<Asset[]>) =>
      assetsAdapter.setAll(state as AssetState, action.payload),
    updateMany: (state, action: PayloadAction<Update<Asset>[]>) =>
      assetsAdapter.updateMany(state as AssetState, action.payload),
    setNewestOne: (state, action: PayloadAction<Asset>) => {
      const currentRevisionNumber =
        state.entities[action.payload.ID]?.Revision?.Number ?? 0;
      const payloadRevisionNumber = action.payload.Revision?.Number ?? 0;

      if (payloadRevisionNumber > currentRevisionNumber) {
        assetsAdapter.setOne(state as AssetState, action.payload);
      }
    },
    setMany: (state, action: PayloadAction<Asset[]>) =>
      assetsAdapter.setMany(state as AssetState, action.payload),
    removeOne: (state, action: PayloadAction<string>) =>
      assetsAdapter.removeOne(state as AssetState, action.payload),
    removeAll: (state) => assetsAdapter.removeAll(state as AssetState),
    initGuardedIDs: (state, action: PayloadAction<string[]>) => {
      state.guardedIDs = action.payload;
    },
    addGuardedIDs: (state, action: PayloadAction<string[]>) => {
      const updatedIDs = uniq(state.guardedIDs.concat(action.payload));
      state.guardedIDs = updatedIDs;
    },
    removeAllButNoGuarded: (state) => {
      const ids = difference(state.ids, state.guardedIDs);
      assetsAdapter.removeMany(state as AssetState, ids);
    },
    setLoading: (state, action: PayloadAction<LoadingStateType>) => {
      state.loading = action.payload;
    },
    startPatientAssetsSnapshot: (
      state,
      action: PayloadAction<{ patientID: string }>,
    ) => {
      state.patientAssetsSnapshot = {
        patientID: action.payload.patientID,
        loading: 'pending',
      };
    },
    completePatientAssetsSnapshot: (
      state,
      action: PayloadAction<{ patientID: string }>,
    ) => {
      if (state.patientAssetsSnapshot.patientID === action.payload.patientID) {
        state.patientAssetsSnapshot.loading = 'succeeded';
        state.patientAssetsSnapshotCompletedGeneration += 1;
      }
    },
    failPatientAssetsSnapshot: (
      state,
      action: PayloadAction<{ patientID: string }>,
    ) => {
      if (state.patientAssetsSnapshot.patientID === action.payload.patientID) {
        state.patientAssetsSnapshot.loading = 'failed';
      }
    },
    startReportAssetsSnapshot: (
      state,
      action: PayloadAction<{ reportID: string }>,
    ) => {
      state.reportAssetsSnapshot = {
        reportID: action.payload.reportID,
        loading: 'pending',
      };
      state.reportAssetsSnapshotPatientCompletionAtRefreshStart = null;
    },
    completeReportAssetsSnapshot: (
      state,
      action: PayloadAction<{ reportID: string }>,
    ) => {
      if (state.reportAssetsSnapshot.reportID === action.payload.reportID) {
        state.reportAssetsSnapshot.loading = 'succeeded';
      }
    },
    failReportAssetsSnapshot: (
      state,
      action: PayloadAction<{ reportID: string }>,
    ) => {
      if (state.reportAssetsSnapshot.reportID === action.payload.reportID) {
        state.reportAssetsSnapshot.loading = 'failed';
      }
    },
    resetReportAssetsSnapshot: (
      state,
      action: PayloadAction<{ reportID: string }>,
    ) => {
      if (state.reportAssetsSnapshot.reportID === action.payload.reportID) {
        state.reportAssetsSnapshot = {
          reportID: '',
          loading: 'idle',
        };
        state.reportAssetsSnapshotPatientCompletionAtRefreshStart = null;
      }
    },
    reset: () => assetsAdapter.getInitialState(makeInitialState()),
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (
        action,
      ): action is PayloadAction<ListReportPanoramasWithConditionsResp> =>
        action.type ===
        `${SliceName.reports}/listReportPanoramasWithConditions/fulfilled`,
      (state, action) =>
        assetsAdapter.setMany(
          state as AssetState,
          action.payload.Items.flatMap((item) => item.Panoramas),
        ),
    );
  },
});

export const { actions } = assetsSlice;

export default assetsSlice.reducer;
